import React, { useEffect, useRef, useState } from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Container,
  TextField,
  Button,
  Divider,
  Chip,
  Menu,
  MenuItem,
  Stack,
  CircularProgress,
  IconButton,
  Tooltip,
  Fab,
} from "@mui/material";
import mapImage from "../../assets/images/map-area.png";
import PhotosMap from "../dashboard/selectArea/PhotosMap";
import { useApp } from "../../Context/AppContext";
import projectData from "./project.json";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  getProjectFiles,
  projectById,
  updateDraftStatus,
  updateProject,
} from "../../apis/apisList/userApi";
import { useParams, useNavigate } from "react-router-dom";
import FileUploadDialog from "./FileUploadDialog";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { toast } from "react-toastify";
import CustomLoader from "../../components/CustomLoader";
import { formatDate } from "../../lib/formatDate";
import ActionButtons from "./ActionButtons";
import DoneIcon from "@mui/icons-material/Done";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DeleteProjectDialog from "./DeleteProjectDialog";
import { deleteProject } from "../../apis/apisList/adminApi";
import ChangePrice from "./ChangePriceModal";
import planConfig from "../../config/planConfig";
import ChangePriceModal from "./ChangePriceModal";
import { getChipProps, getUserStatusLable } from "../../config/helper";
import {
  AddCircle,
  Check,
  DockOutlined,
  DownloadDone,
  DownloadOutlined,
} from "@mui/icons-material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import QuotationDialog from "../dashboard/selectArea/QuotationDialog";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { Download } from "lucide-react";
import ServiceDialog from "./ServiceDialog";
import ProposalAccessDialog from "../proposal/ProposalAccessDialog";

const generateKMLFiles = (data) => {
  data.forEach((plan) => {
    const fileName = `${plan.selectedOption.replace(/\s+/g, "_")}.kml`; // Example: "Photos_Plan.kml"

    const kmlHeader = `<?xml version="1.0" encoding="UTF-8"?>
    <kml xmlns="http://www.opengis.net/kml/2.2">
      <Document>
    `;

    const kmlFooter = `</Document></kml>`;

    let kmlBody = "";

    plan.features.forEach((feature) => {
      const { geometry, properties } = feature;

      if (geometry.type === "Point") {
        kmlBody += `
          <Placemark>
            <name>Point - ${properties?.id || "Unknown"}</name>
            <Point>
              <coordinates>${geometry.coordinates[0]},${
          geometry.coordinates[1]
        },0</coordinates>
            </Point>
          </Placemark>
        `;
      } else if (geometry.type === "LineString") {
        kmlBody += `
          <Placemark>
            <name>Line - ${properties?.id || feature.id || "Unknown"}</name>
            <LineString>
              <coordinates>
                ${geometry.coordinates
                  .map((coord) => `${coord[0]},${coord[1]},0`)
                  .join(" ")}
              </coordinates>
            </LineString>
          </Placemark>
        `;
      } else if (geometry.type === "Polygon") {
        kmlBody += `
          <Placemark>
            <name>Polygon - ${properties?.id || feature.id || "Unknown"}</name>
            <Polygon>
              <outerBoundaryIs>
                <LinearRing>
                  <coordinates>
                    ${geometry.coordinates[0]
                      .map((coord) => `${coord[0]},${coord[1]},0`)
                      .join(" ")}
                  </coordinates>
                </LinearRing>
              </outerBoundaryIs>
            </Polygon>
          </Placemark>
        `;
      }
    });

    const kmlContent = kmlHeader + kmlBody + kmlFooter;
    const blob = new Blob([kmlContent], {
      type: "application/vnd.google-earth.kml+xml",
    });

    // Create download link
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  });
};

const ProjectPage = () => {
  // const { data, projectName: title } = projectData;
  const [quoteDialog, setQuoteDialog] = useState(false);
  const { user, setCurrentStep, setSelectedOption, setProjectData } = useApp();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [status, setStatus] = useState("Pending");
  const [project, setProject] = useState(null);
  const [plans, setPlans] = useState([]);
  const { id } = useParams();
  const admin = localStorage.getItem("admin");
  const [openFileUpload, setOpenFileUpload] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [address, setAddress] = useState(project?.name || "");
  const inputRef = useRef(null);
  const [isQuoteUpdating, setIsQuoteUpdating] = useState(false);
  const [fetchTrigger, setFetchTrigger] = useState(false);
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [originalName, setOriginalName] = useState("");
  const [originalAddress, setOriginalAddress] = useState("");

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [proposalDialog, setProposalDialog] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  const [isChanged, setIsChanged] = useState(false);
  const [isChangedAddress, setIsChangedAddress] = useState(false);

  useEffect(() => {
    setIsChanged(projectName !== originalName);
  }, [projectName, originalName]);
  useEffect(() => {
    setIsChangedAddress(address !== originalAddress);
  }, [address, originalAddress]);

  const handleSave = async () => {
    setOriginalName(projectName);
    try {
      await updateProject(id, { name: projectName });
      toast.success("Project name changed successfully");
    } catch (error) {
      console.log("error saving");
      toast.error("Error while changing project name");
    }
  };
  const handleSaveAddress = async () => {
    setOriginalAddress(address);
    try {
      await updateProject(id, { address: address });
      toast.success("Address changed successfully");
    } catch (error) {
      console.log("error saving", error);
      toast.error("Error while changing Address");
    }
  };

  const handleOpenMenu = (event) => {
    if (admin) {
      setMenuAnchor(event.currentTarget);
    }
  };

  const handleAddMore = () => {
    setSelectedOption(null);
    setProjectData(plans);
    localStorage.setItem("edit_project", true);
    localStorage.setItem("edit_id", project?._id);
    localStorage.setItem(
      "projectData",
      JSON.stringify({
        data: plans,
        projectName: project.name,
        address: project.address,
      })
    );
    setCurrentStep(2);
    localStorage.setItem("project_add_more", true);
    navigate("/");
  };

  const handleDownload = () => {
    generateKMLFiles(plans);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setIsUpdating(true);
      updateProject(id, { status: newStatus }).then((res) => {
        if (res?.success) {
          setStatus(newStatus);
          setMenuAnchor(null);
          toast.success("Status updated successfully");
          setIsUpdating(false);
          setFetchTrigger((prev) => !prev);
        } else {
          toast.error(res?.message);
          setFetchTrigger((prev) => !prev);
          setMenuAnchor(null);
          setIsUpdating(false);
        }
      });
    } catch (error) {
      setIsUpdating(false);
      toast.error("Something went wrong.");
      // console.log("Error updating project status:", error);
    }
  };

  const handleFileDownload = async () => {
    setDownloading(true);
    toast.success("Downloading files...", {
      autoClose: 2000,
      toastId: "downloading",
    });

    try {
      project.files.forEach(({ name, url }) => {
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", name); // Optional: Some browsers ignore this for external links
        // link.target = "_blank"; // Opens in a new tab
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
    } catch (error) {
      toast.dismiss("downloading");
      toast.error("Error downloading files");
      console.error("Error downloading files:", error);
    } finally {
      setDownloading(false);
    }
  };

  const handleProjectName = async () => {
    try {
      await updateProject(id, { name: projectName });
      setIsEdit(false);
    } catch (error) {
      console.log("error change project name");
    }
  };

  const handleRequest = () => {
    setQuoteDialog(true);
  };

  const handleRequestQuote = async (data) => {
    if (!id) return false;
    try {
      setIsQuoteUpdating(true);

      await updateDraftStatus(id, data);
      setIsQuoteUpdating(false);
      toast.success("Quote Submitted successfully!");
      setFetchTrigger((prev) => !prev);
      return true;
    } catch (error) {
      setIsQuoteUpdating(false);
      toast.error(
        "Oops. Something went wrong. Please screenshot this screen and email (please note property address) to flights@flyover.io and we’ll send you a quote, or call us at (608) 571 7992",
        {
          autoClose: false,
        }
      );
      console.error("Error during Submitted quote", error);
      return false;
    }
  };

  const handleAccept = async (value) => {
    if (!project) return;
    try {
      updateProject(project._id, {
        userStatus: value,
      }).then((res) => {
        if (res?.success) {
          setFetchTrigger(!fetchTrigger);
          if (value === "Rejected") {
            //
          } else {
            toast.success(
              "Great, we’ll be in touch shortly to discuss scheduling!"
            );
          }
        }
      });
    } catch (error) {
      toast.error("Error accepting");
    }
  };

  const handleDeleteProject = async () => {
    try {
      setIsDeleting(true);
      await deleteProject(id);
      toast.success("Project deleted successfully");
      navigate("/admindashboard");
    } catch (error) {
      toast.error("Failed to delete project");
      console.error("Error deleting project:", error);
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };
  useEffect(() => {
    const getProject = async () => {
      try {
        let { result } = await projectById(id);
        setProjectName(result.name);
        setOriginalName(result.name);
        setProject(result);
        setStatus(result.status);
        setPlans(JSON.parse(result.plans));
        setAddress(result.address);
        setOriginalAddress(result.address);
      } catch (error) {
        if (error.status === 401) {
          setAccessDenied(true);
        }
        console.error("Error fetching project:", error);
        setProject(null);
      } finally {
        setLoading(false);
      }
    };
    if (id) {
      getProject();
    }
  }, [id, fetchTrigger]);

  useEffect(() => {
    if (isEdit && inputRef.current) {
      inputRef.current.focus(); // Automatically focus the input
      inputRef.current.select(); // Select all text
    }
  }, [isEdit]); // Runs whenever isEdit changes

  const [selectPrice, setSelectPrice] = useState({
    index: 0,
    result: "",
  });
  const getFiles = async () => {
    try {
      let res = await getProjectFiles(project?._id);

      return res.results;
    } catch (error) {
      console.log("error get files", error);
      return [];
    }
  };

  const handleZipDownload = async () => {
    setDownloading(true);
    // Start persistent toast
    const toastId = toast.loading("Downloading files...");
    try {
      const files = await getFiles();
      const fileName = `${project?.contact?.fullName}/${project?.name}.zip`;
      if (files.length === 0) {
        toast.update(toastId, {
          render: "No files available yet for download",
          type: "info",
          isLoading: false,
          autoClose: 3000,
        });
        return;
      }
      const zip = new JSZip();
      for (const { name, url } of files) {
        const response = await fetch(url);
        const blob = await response.blob();
        zip.file(name, blob);
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });

      saveAs(zipBlob, fileName);

      toast.update(toastId, {
        render: "Download complete!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error) {
      toast.update(toastId, {
        render: "Error downloading files",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
      console.error("Error creating ZIP archive:", error);
    } finally {
      setDownloading(false);
    }
  };

  const handleChangePriceModal = (result, index) => {
    setIsOpen(true);
    setSelectPrice({
      index: index,
      result: result,
    });
  };
  if (loading) {
    return (
      <Box
        sx={{
          height: "70vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CustomLoader loading={loading} />
      </Box>
    );
  }

  if (!project) {
    return (
      <Box sx={{ height: "calc(100vh - 64px)" }}>
        {accessDenied ? (
          <ProposalAccessDialog
            open={proposalDialog}
            // onClose={() => setProposalDialog(false)}
            loginRedirectUrl={window.location.pathname}
            projectId={id}
          />
        ) : (
          <Typography variant="h4" align="center">
            No project found
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ padding: 4 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          mb: 4,
          width: "100%",
        }}
      >
        {/* Project Name */}
        <>
          <Box sx={{ flex: 1, position: "relative" }}>
            <TextField
              label="Project Name"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              variant="standard"
              fullWidth
              InputLabelProps={{
                shrink: true,
                sx: { fontSize: "0.9rem", color: "text.secondary" },
              }}
              InputProps={{
                sx: {
                  fontSize: "1.25rem",
                  fontWeight: 600,
                },
              }}
              onBlur={() => {
                if (isChanged) {
                  handleSave();
                }
              }}
            />
            {/* {isChanged && (
              <IconButton
                onClick={handleSave}
                color="primary"
                sx={{ position: "absolute", right: 6, top: 8 }}
              >
                <Check />
              </IconButton>
            )} */}
          </Box>
        </>

        {/* Address */}
        <Box sx={{ flex: 1, position: "relative" }}>
          <TextField
            label="Address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            variant="standard"
            fullWidth
            InputLabelProps={{
              shrink: true,
              sx: { fontSize: "0.9rem", color: "text.secondary" },
            }}
            InputProps={{
              sx: {
                fontSize: "1.25rem",
                fontWeight: 600,
              },
            }}
            onBlur={() => {
              if (isChangedAddress) {
                handleSaveAddress();
              }
            }}
          />
          {/* {isChangedAddress && (
            <IconButton
              onClick={handleSaveAddress}
              color="primary"
              sx={{ position: "absolute", right: 6, top: 8 }}
            >
              <Check />
            </IconButton>
          )} */}
        </Box>
      </Box>

      {plans?.length > 0 ? (
        <Grid container spacing={4}>
          {/* Left Side - Image Cards */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              {plans?.map(
                (
                  {
                    result,
                    features,
                    selectedOption,
                    zoomLevel,
                    mainServiceLabel,
                    subServiceLabel,
                    id,
                    from_new_ui = false,
                  },
                  index
                ) => (
                  <Grid item xs={12} sm={6} key={id || index}>
                    <Card
                      sx={{
                        borderRadius: 2,
                        boxShadow: 3,
                        padding: "10px",
                        position: "relative",
                      }}
                    >
                      <PhotosMap
                        features={features}
                        zoomLevel={zoomLevel}
                        type={selectedOption}
                      />
                      <ActionButtons
                        type={selectedOption}
                        project={project}
                        plans={plans}
                        setOpenFileUpload={setOpenFileUpload}
                        id={id}
                        from_new_ui={from_new_ui}
                      />
                    </Card>
                    <Typography
                      variant="h6"
                      sx={{ textAlign: "center", marginTop: "5px" }}
                    >
                      {result.type}
                      {(mainServiceLabel || subServiceLabel) && (
                        <Typography variant="body2" color="text.secondary">
                          {mainServiceLabel}
                          {mainServiceLabel && subServiceLabel ? " • " : ""}
                          {subServiceLabel}
                        </Typography>
                      )}
                    </Typography>
                  </Grid>
                )
              )}

              {/* {project.status === "pending" && (
                <Grid item xs={12} sm={6}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      boxShadow: 4,
                      height: 310,
                      p: 3,
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      bgcolor: "background.paper",
                      "&:hover": {
                        boxShadow: 6,
                        transform: "translateY(-4px)",
                        bgcolor: "grey.100",
                      },
                    }}
                    onClick={() => handleAddMore("new")}
                  >
                    <AddCircle sx={{ fontSize: 40, color: "primary.main" }} />
                    <Box
                      sx={{
                        fontWeight: 500,
                        fontSize: "1rem",
                        color: "text.primary",
                      }}
                    >
                      Add More Services
                    </Box>
                  </Card>
                </Grid>
              )} */}
            </Grid>
          </Grid>

          {/* Right Side - Project Summary */}
          <Grid item xs={12} md={4} gap={0}>
            <Card
              sx={{
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                mb: 2,
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    bgcolor: "#222",
                    color: "white",
                    p: 2,
                    fontWeight: 500,
                    fontSize: "1.25rem",
                    textAlign: "center",
                  }}
                >
                  Project Details
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    p: 2,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Project status:
                    </Typography>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-end",
                        position: "relative",
                        cursor: "pointer",
                      }}
                      onClick={(event) => handleOpenMenu(event)}
                    >
                      <div
                        style={{
                          position: "relative",
                        }}
                      >
                        {project.isDraft ? (
                          <Chip label="Draft" size="small" variant="outlined" />
                        ) : (
                          <Chip
                            label={getUserStatusLable(
                              project.status,
                              project.userStatus
                            )}
                            // color={
                            //   status === "completed"
                            //     ? "success" // Green for completed (✔)
                            //     : status === "pending"
                            //     ? "warning" // Orange for pending (⏳)
                            //     : status === "accepted"
                            //     ? "primary" // Blue for accepted (👍)
                            //     : status === "rejected"
                            //     ? "error" // Red for rejected (❌)
                            //     : "default" // Gray for unknown status
                            // }
                            variant="outlined"
                            size="small"
                            onDelete={admin ? () => {} : undefined}
                            deleteIcon={
                              admin ? <ArrowDropDownIcon /> : undefined
                            }
                            {...getChipProps(
                              project.status,
                              project.userStatus
                            )}
                            style={{
                              textTransform: "capitalize !important",
                            }}
                          />
                        )}

                        {isUpdating && (
                          <CircularProgress
                            size={18}
                            thickness={5}
                            color="secondary"
                            style={{
                              position: "absolute",
                              right: "-30px",
                              top: 2,
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  {admin && (
                    <Box sx={{ p: 2, background: "#f5f5f5", borderRadius: 2 }}>
                      <Typography variant="h6" fontWeight={600} mb={1}>
                        Client Details
                      </Typography>

                      <Typography variant="body2">
                        <strong>Name:</strong>{" "}
                        {project?.contact?.fullName || "N/A"}
                      </Typography>

                      <Typography variant="body2">
                        <strong>Email:</strong>{" "}
                        <a
                          href={`mailto:${project?.contact?.email}`}
                          style={{ color: "#0277BD" }}
                        >
                          {project?.contact?.email || "N/A"}
                        </a>
                      </Typography>

                      <Typography variant="body2">
                        <strong>Phone:</strong>{" "}
                        <a
                          href={`tel:${project?.contact?.phone}`}
                          style={{ color: "#0277BD" }}
                        >
                          {project?.contact?.phone || "N/A"}
                        </a>
                      </Typography>

                      <Typography variant="body2">
                        <strong>Address:</strong> {project?.address || "N/A"}
                      </Typography>

                      <Typography variant="body2">
                        <strong>About the project:</strong>{" "}
                        {project?.about || "N/A"}
                      </Typography>
                    </Box>
                  )}
                  {admin ? (
                    <>
                      {project?.proposalInfo && (
                        <Box
                          sx={{ p: 2, background: "#f5f5f5", borderRadius: 2 }}
                        >
                          <Typography variant="h6" fontWeight={600} mb={1}>
                            Proposal Details
                          </Typography>
                          <Typography variant="body2">
                            <strong>Submitted Date:</strong>{" "}
                            {formatDate(project?.proposalInfo?.proposal_date) ||
                              "N/A"}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Proposal Date:</strong>{" "}
                            {formatDate(project?.proposalInfo?.accepted_date) ||
                              "N/A"}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Viewed Date:</strong>{" "}
                            {formatDate(
                              project?.proposalInfo?.user_view_date
                            ) || "N/A"}
                          </Typography>
                          {project?.proposalInfo?.user_rejected_date && (
                            <Typography variant="body2">
                              <strong>Declined Date:</strong>{" "}
                              {formatDate(
                                project?.proposalInfo?.user_rejected_date
                              ) || "N/A"}
                            </Typography>
                          )}
                          {project?.proposalInfo?.user_accepted_date && (
                            <Typography variant="body2">
                              <strong>Accepted Date:</strong>{" "}
                              {formatDate(
                                project?.proposalInfo?.user_accepted_date
                              ) || "N/A"}
                            </Typography>
                          )}
                          {project?.proposalInfo?.completed_date && (
                            <Typography variant="body2">
                              <strong>Completed Date:</strong>{" "}
                              {formatDate(
                                project?.proposalInfo?.completed_date
                              ) || "N/A"}
                            </Typography>
                          )}
                          <Typography variant="body2">
                            <strong>Proposal link:</strong>{" "}
                            <a
                              href={project?.proposalInfo?.adminLink}
                              target="_blank"
                            >
                              link
                            </a>
                          </Typography>
                          <Typography variant="body2">
                            <strong>Proposal PDF:</strong>{" "}
                            <a
                              href={`/proposal?id=${project?._id}`}
                              target="_blank"
                            >
                              link
                            </a>
                          </Typography>
                        </Box>
                      )}
                    </>
                  ) : (
                    <>
                      {project?.proposalInfo && (
                        <Box
                          sx={{ p: 2, background: "#f5f5f5", borderRadius: 2 }}
                        >
                          <Typography variant="h6" fontWeight={600} mb={1}>
                            Proposal Details
                          </Typography>
                          <Typography
                            variant="body2"
                            style={{ textTransform: "capitalize" }}
                          >
                            <strong>Status:</strong>{" "}
                            {project?.proposalInfo?.status || "N/A"}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Sent Date:</strong>{" "}
                            {formatDate(project?.proposalInfo?.date) || "N/A"}
                          </Typography>
                          {/* <Typography variant="body2">
                            <strong>Viewed Date:</strong>{" "}
                            {formatDate(project?.proposalInfo?.viewedAt) ||
                              "N/A"}
                          </Typography> */}
                          <Typography variant="body2">
                            <strong>Accepted Date:</strong>{" "}
                            {formatDate(project?.proposalInfo?.accepted_date) ||
                              "N/A"}
                          </Typography>
                          {/* <Typography variant="body2">
                            <strong>Proposal link:</strong>{" "}
                            <a
                              href={project?.proposalInfo?.link}
                              target="_blank"
                            >
                              link
                            </a>
                          </Typography> */}
                        </Box>
                      )}
                    </>
                  )}
                </Box>

                <Box sx={{ p: 2 }}>
                  {plans.map(
                    (
                      { result, mainServiceLabel, subServiceLabel, id },
                      index
                    ) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                          p: 2,
                          mb: 2,
                          borderRadius: 2,
                          boxShadow: 1,
                          bgcolor: "background.paper",
                        }}
                      >
                        {/* Image */}
                        <Box
                          component="img"
                          src={mapImage}
                          alt={result.type}
                          sx={{
                            width: 100,
                            height: 70,
                            borderRadius: 1,
                            border: "1px solid #ddd",
                            objectFit: "cover",
                          }}
                        />

                        {/* Plan Details */}
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight={600}>
                            {result.type}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            {mainServiceLabel}
                            {mainServiceLabel && subServiceLabel ? " • " : ""}
                            {subServiceLabel}
                          </Typography>

                          {/* Conditional Info */}
                          <Typography variant="body2" color="text.secondary">
                            {result.type === "Photos Plan" ? (
                              <>Photos: {result.totalImages}</>
                            ) : result.type === "Videos Plan" ? (
                              <>Miles: {parseFloat(result?.area).toFixed(2)}</>
                            ) : result.type ===
                              planConfig?.ground_control
                                ?.ground_control_points_plan ? (
                              <>Photos: {result.totalImages}</>
                            ) : (
                              <>Acres: {parseFloat(result?.area).toFixed(2)}</>
                            )}
                          </Typography>

                          {/* Price */}
                          {(project.status !== "pending" || admin) && (
                            <Typography variant="body2" color="text.secondary">
                              {result.type ===
                                planConfig?.ground_control
                                  ?.ground_control_points_plan && admin ? (
                                // <>
                                <>Price: ${result.price}</>
                              ) : (
                                //   Per Photo Price: $
                                //   {result.each_price * result.totalImages} (
                                //   {result.totalImages} x ${result.each_price})
                                // </>
                                <>Price: ${result.price}</>
                              )}
                            </Typography>
                          )}

                          {/* Admin Buttons */}
                          {admin && (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 2,
                                mt: 1,
                                flexWrap: {
                                  xs: "wrap",
                                  sm: "nowrap",
                                },
                              }}
                            >
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                style={{
                                  height: "40px",
                                  whiteSpace: "nowrap",
                                  marginLeft: "-10px",
                                }}
                                onClick={() =>
                                  handleChangePriceModal(result, index)
                                }
                              >
                                Change Price
                              </Button>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() =>
                                  setSelectedPlan({ ...result, id })
                                }
                                style={{
                                  height: "40px",
                                  whiteSpace: "nowrap",
                                  marginLeft: "-10px",
                                }}
                              >
                                Manage Services
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* Total Cost */}
                  {(project.status !== "pending" || admin) && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      <Typography variant="h6" fontWeight={500}>
                        Total Cost:
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        ${project?.price}
                      </Typography>
                    </Box>
                  )}

                  {/* Project Reason */}
                  {project?.reason && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Project Price Change Info:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {project?.reason}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
            <Card
              sx={{
                padding: 2,
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                borderBottomLeftRadius: 8,
                borderBottomRightRadius: 8,
                position: "relative",
                top: "-15px",
                bgcolor: "#F0F4F9",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-evenly",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "100px",
                  }}
                >
                  {project.isDraft && !admin ? (
                    <Button
                      fullWidth
                      variant="contained"
                      sx={{
                        mb: 1.5,
                        bgcolor: "#065F9C",
                        borderRadius: 3,
                        textTransform: "none",
                        py: 1.3,
                        "&:hover": {
                          bgcolor: "#005bb9",
                        },
                        maxWidth: "90%",
                        whiteSpace: "nowrap",
                      }}
                      loading={isQuoteUpdating}
                      loadingPosition="end"
                      onClick={handleRequest}
                    >
                      Request a Quote
                    </Button>
                  ) : (
                    // {/* User can accept or reject */}
                    // project.status === "pending" && (
                    <div>
                      {project.userStatus ? (
                        <Button variant="outlined" disabled>
                          {project.userStatus}
                        </Button>
                      ) : (
                        !admin &&
                        project.status !== "pending" && (
                          <div
                            style={{
                              display: "flex",
                              gap: "10px",
                            }}
                          >
                            <Button
                              variant="contained"
                              color="success"
                              onClick={() => handleAccept("Accepted")}
                            >
                              Accept
                            </Button>
                            <Button
                              variant="contained"
                              color="error"
                              onClick={() => handleAccept("Rejected")}
                            >
                              No Thanks
                            </Button>
                          </div>
                        )
                      )}
                    </div>
                    // )
                  )}
                </div>
                {admin && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: project.status === "pending" ? "20px" : "0",
                    }}
                  >
                    {project.status === "pending" && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleStatusChange("accepted")}
                        disabled={project?.deletedAt}
                      >
                        Send Quote
                      </Button>
                    )}
                    {project?.deletedAt ? (
                      <Button variant="outlined" disabled>
                        Archived
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => setDeleteDialogOpen(true)}
                      >
                        Delete Project
                      </Button>
                    )}
                    <DeleteProjectDialog
                      open={deleteDialogOpen}
                      onClose={() => setDeleteDialogOpen(false)}
                      onConfirm={handleDeleteProject}
                      loading={isDeleting}
                      projectName={projectName}
                    />
                  </div>
                )}
              </div>
            </Card>
            {project?.proposal_pdf &&
              (project.status === "accepted" || admin) && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Fab
                    variant="extended"
                    color="primary"
                    onClick={() =>
                      window.open(`/proposal?id=${project._id}`, "_blank")
                    }
                  >
                    <PictureAsPdfIcon sx={{ mr: 1 }} />
                    Download Proposal
                  </Fab>
                </Box>
              )}

            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              {/* <MenuItem onClick={() => handleStatusChange("pending")}>
                Pending
              </MenuItem> */}
              <MenuItem onClick={() => handleStatusChange("accepted")}>
                Accepted
              </MenuItem>
              <MenuItem onClick={() => handleStatusChange("rejected")}>
                Rejected
              </MenuItem>
              <MenuItem onClick={() => handleStatusChange("completed")}>
                Complete
              </MenuItem>
            </Menu>
            <FileUploadDialog
              open={openFileUpload}
              onClose={() => setOpenFileUpload(false)}
              projectId={id}
              project={project}
            />
            {project?.status === "completed" && (
              <Button
                sx={{
                  textAlign: "center",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
                variant="contained"
                onClick={handleZipDownload}
                disabled={downloading}
              >
                <Download />
                <span>Download all data</span>
              </Button>
            )}
          </Grid>
        </Grid>
      ) : (
        <Box textAlign="center" mt={4}>
          <Typography variant="h4">Record not found</Typography>
        </Box>
      )}
      <ChangePriceModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        fetchTrigger={fetchTrigger}
        selectPrice={selectPrice}
        setFetchTrigger={setFetchTrigger}
      />
      {selectedPlan && (
        <ServiceDialog
          open={selectedPlan}
          onClose={() => setSelectedPlan(null)}
          fetchTrigger={fetchTrigger}
          selectPrice={selectPrice}
          setFetchTrigger={setFetchTrigger}
          planId={selectedPlan?.id}
        />
      )}

      <QuotationDialog
        open={quoteDialog}
        onClose={() => setQuoteDialog(false)}
        projectName={projectName}
        address={address}
        handleRequestQuote={handleRequestQuote}
      />
    </Container>
  );
};

export default ProjectPage;
